import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Button,
  TextField,
  InputAdornment,
  Pagination,
} from "@mui/material";
import {
  Link as LinkIcon,
  AssignmentOutlined as FormIcon,
  VisibilityOutlined as ViewIcon,
  Search,
} from "@mui/icons-material";
import { useAppContext } from "../../context/AppContext";
import { GetAllUserAppointment } from "../../helpers/Appointment";
import moment from "moment";
import Toast from "../../components/toast";
import { GetUpcomingAppointmentsByUserId } from "../../helpers/AppointmentSchedule";
import { downloadConsentFormByChatId } from "../../helpers/Downloads";
import { minutesToTime } from "../../constants/timeUtils";

const UpcomingAppointments = () => {
  const navigate = useNavigate();
  const { isMobile } = useAppContext();

  const [isLoading, setIsLoading] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const rowsPerPage = 5;

  useEffect(() => {
    GetInitialData();
  }, []);

  const GetInitialData = async () => {
    setIsLoading(true);

    const response = await GetUpcomingAppointmentsByUserId();
    if (response.statusCode === 200 && response.isSuccess === true) {
      const data = response.data;
      setAppointments(data ?? []);
    } else {
      Toast.error(response.message);
    }

    // setAppointments([
    //   {
    //     _id: "69a291d158a62d2efb6db43e",
    //     chatID: "698e0e3e27cff986dd95f037",
    //     calendlyInviteURI: "testingURI",
    //     appointmentDate: "2026-02-28T06:57:21.000Z",
    //     zoomLink: "https//zoom.link.com",
    //     status: "active",
    //   },
    //   {
    //     _id: "69a291d158a62d2efb6db43e",
    //     chatID: "698e0e3e27cff986dd95f037",
    //     calendlyInviteURI: "testingURI",
    //     appointmentDate: "2026-02-28T06:57:21.000Z",
    //     zoomLink: "https//zoom.link.com",
    //     status: "pending",
    //   },
    //   {
    //     _id: "69a291d158a62d2efb6db43e",
    //     chatID: "698e0e3e27cff986dd95f037",
    //     calendlyInviteURI: "testingURI",
    //     appointmentDate: "2026-02-28T06:57:21.000Z",
    //     zoomLink: "https//zoom.link.com",
    //     status: "active",
    //   },
    //   {
    //     _id: "69a291d158a62d2efb6db43e",
    //     chatID: "698e0e3e27cff986dd95f037",
    //     calendlyInviteURI: "testingURI",
    //     appointmentDate: "2026-02-28T06:57:21.000Z",
    //     zoomLink: "https//zoom.link.com",
    //     status: "active",
    //   },
    //   {
    //     _id: "69a291d158a62d2efb6db43e",
    //     chatID: "698e0e3e27cff986dd95f037",
    //     calendlyInviteURI: "testingURI",
    //     appointmentDate: "2026-02-28T06:57:21.000Z",
    //     zoomLink: "https//zoom.link.com",
    //     status: "pending",
    //   },
    //   {
    //     _id: "69a291d158a62d2efb6db43e",
    //     chatID: "698e0e3e27cff986dd95f037",
    //     calendlyInviteURI: "testingURI",
    //     appointmentDate: "2026-02-28T06:57:21.000Z",
    //     zoomLink: "https//zoom.link.com",
    //     status: "active",
    //   },
    //   {
    //     _id: "69a291d158a62d2efb6db43e",
    //     chatID: "698e0e3e27cff986dd95f037",
    //     calendlyInviteURI: "testingURI",
    //     appointmentDate: "2026-02-28T06:57:21.000Z",
    //     zoomLink: "https//zoom.link.com",
    //     status: "active",
    //   },
    // ]);

    setIsLoading(false);
  };

  const filteredAppointments = useMemo(() => {
    return appointments.filter((item) =>
      moment(item.appointmentDate)
        .format("DD MMM YYYY, h:mm A")
        .toLowerCase()
        .includes(search.toLowerCase()),
    );
  }, [appointments, search]);

  const paginatedAppointments = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return filteredAppointments.slice(start, start + rowsPerPage);
  }, [filteredAppointments, page]);

  const pageCount = Math.ceil(filteredAppointments.length / rowsPerPage);

  const styles = {
    tableCell: {
      color: "#6B6B6B",
      fontSize: "14px",
      fontWeight: 500,
      whiteSpace: "nowrap",
    },
  };

  const handleViewClick = (row) => {
    navigate("/upcoming-appointment-detail", {
      state: { appointmentId: row._id },
    });
  };

  const handleViewConsentForm = async (row) => {
    downloadConsentFormByChatId(row.chatId);
  };

  const handleJoinMeeting = async (row) => {
    if (!row.zoomJoinUrl) {
      Toast.error("Zoom link is not available");
      return;
    }

    // Open in new tab
    window.open(row.zoomJoinUrl, "_blank");
  };

  return (
    <Box>
      <Box
        sx={{
          width: "100%",
          border: "1px solid #E8E8E8",
          borderRadius: "12px",
          padding: "10px 10px",
          backgroundColor: "#fff",
        }}
      >
        <Box
          sx={{
            my: 1,
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            justifyContent: "space-between",
            alignItems: isMobile ? "flex-start" : "center",
            gap: 2,
          }}
        >
          <Typography fontSize={"24px"} className="sf-medium sf-medium--dark">
            Upcoming Appointments
          </Typography>

          <TextField
            size="small"
            placeholder="Search..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: "#999" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: isMobile ? "100%" : 260,
              background: "#fff",
              borderRadius: "8px",
            }}
          />
        </Box>

        <TableContainer sx={{ overflowX: "auto" }}>
          <Table
            aria-label="upcoming appointments table"
            sx={{
              border: "1px solid #E8E8E8",
              borderRadius: "12px",
              overflow: "hidden",
              minWidth: 900,
            }}
          >
            <TableHead sx={{ backgroundColor: "#F5F5FF" }}>
              <TableRow>
                <TableCell sx={{ ...styles.tableCell, width: "25%" }}>
                  Appointment Date & Time
                </TableCell>
                <TableCell sx={{ ...styles.tableCell }}>Patient Name</TableCell>
                <TableCell sx={{ ...styles.tableCell }}>Zoom Link</TableCell>
                <TableCell sx={{ ...styles.tableCell }}>Consent Form</TableCell>
                <TableCell sx={{ ...styles.tableCell }}>Status</TableCell>
                <TableCell sx={{ ...styles.tableCell }}>Action</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {paginatedAppointments.length > 0 ? (
                paginatedAppointments.map((row, idx) => (
                  <TableRow
                    key={`${row._id}-${idx}`}
                    sx={{
                      "&:last-child td, &:last-child th": { border: 0 },
                    }}
                  >
                    <TableCell>
                      {moment.utc(row.appointmentDate).format("DD MMM YYYY") +
                        ", " +
                        minutesToTime(row.startMinute) +
                        " - " +
                        minutesToTime(row.endMinute)}
                    </TableCell>
                    <TableCell>{row.patientName}</TableCell>
                    <TableCell>
                      <Button
                        sx={{
                          boxShadow: "0px 1px 5px rgb(0 0 0 / 5%)",
                          border: "1px solid #E3E3E5",
                          textTransform: "none",
                          borderRadius: "6px",
                          whiteSpace: "nowrap",
                          color: "#1E1E1EBF",
                          minWidth: "120px",
                          px: 1.5,
                        }}
                        onClick={() => handleJoinMeeting(row)}
                      >
                        <LinkIcon sx={{ mr: 0.5, fontSize: 20 }} />
                        Join Meeting
                      </Button>
                    </TableCell>
                    <TableCell>
                      <Button
                        sx={{
                          boxShadow: "0px 1px 5px rgb(0 0 0 / 5%)",
                          border: "1px solid #E3E3E5",
                          textTransform: "none",
                          borderRadius: "6px",
                          whiteSpace: "nowrap",
                          color: "#1E1E1EBF",
                          minWidth: "130px",
                          px: 1.5,
                        }}
                        onClick={() => handleViewConsentForm(row)}
                      >
                        <FormIcon
                          sx={{ mr: 0.5, fontSize: 18, color: "#1E1E1EBF" }}
                        />
                        Consent Form
                      </Button>
                    </TableCell>

                    <TableCell>
                      <Box
                        sx={{
                          backgroundColor:
                            row.status === "active" ? "#DDF7E6" : "#FFF3DF",
                          px: 1.5,
                          py: 0.5,
                          borderRadius: "6px",
                          fontSize: "13px",
                          width: "fit-content",
                          color:
                            row.status === "active" ? "#01AC3C" : "#FFAA00",
                          textTransform: "capitalize",
                        }}
                      >
                        {row.status === "active" ? "Completed" : row.status}
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Button
                        sx={{
                          boxShadow: "0px 1px 5px rgb(0 0 0 / 5%)",
                          border: "1px solid #E3E3E5",
                          textTransform: "none",
                          borderRadius: "6px",
                          color: "#1E1E1EBF",
                          minWidth: "80px",
                          px: 1.5,
                        }}
                        onClick={() => handleViewClick(row)}
                      >
                        <ViewIcon sx={{ mr: 0.5, fontSize: 18 }} />
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    {isLoading ? "Loading..." : "No appointments found"}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {pageCount > 0 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: 3,
            }}
          >
            <Pagination
              count={pageCount}
              page={page}
              onChange={(e, value) => setPage(value)}
              color="primary"
              variant="outlined"
              siblingCount={1}
              boundaryCount={1}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default UpcomingAppointments;
